import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { formatDateTime, formatValue } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusChip } from "@/components/common/StatusChip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const TONES = {
  create: "green",
  update: "blue",
  delete: "amber",
  login: "gray",
  login_failed: "red",
  access_denied: "red",
  state_change: "amber",
  qbo_conectado: "green",
  qbo_desconectado: "amber",
  qbo_sync_clientes: "blue",
  qbo_invoice_emitida: "green",
  qbo_invoice_error: "red",
  qbo_invoice_cobrada: "green",
};

export default function AuditPage() {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [userId, setUserId] = useState("all");
  const [entidad, setEntidad] = useState("all");
  const [accion, setAccion] = useState("all");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [incluirSistema, setIncluirSistema] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(q);
      setPage(1);
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const pageSize = 25;
  const { data, isLoading } = useQuery({
    queryKey: ["audit", { debounced, userId, entidad, accion, desde, hasta, page, incluirSistema }],
    queryFn: async () =>
      (
        await api.get("/audit", {
          params: {
            q: debounced || undefined,
            user_id: userId === "all" ? undefined : userId,
            entidad: entidad === "all" ? undefined : entidad,
            accion: accion === "all" ? undefined : accion,
            origen: incluirSistema ? "all" : "humano",
            desde: desde || undefined,
            hasta: hasta || undefined,
            page,
            page_size: pageSize,
          },
        })
      ).data,
  });

  const { data: facets } = useQuery({
    queryKey: ["audit-facets"],
    queryFn: async () => (await api.get("/audit/facets")).data,
  });

  const columns = [
    {
      key: "timestamp",
      label: t("audit.date"),
      width: 165,
      render: (r) => <span className="tabular-nums text-ink-tertiary">{formatDateTime(r.timestamp)}</span>,
    },
    {
      key: "accion",
      label: t("audit.action"),
      width: 145,
      render: (r) => <StatusChip tone={TONES[r.accion] || "gray"} label={t(`audit.actions.${r.accion}`)} />,
    },
    { key: "entidad", label: t("audit.entity"), width: 135 },
    {
      key: "entity_code",
      label: t("audit.entity_code"),
      render: (r) => (
        <span className="font-medium">
          {r.entity_code || r.detalle || "—"}
          {r.entity_label && <span className="ml-1.5 font-normal text-ink-tertiary">· {r.entity_label}</span>}
        </span>
      ),
    },
    {
      key: "user_nombre",
      label: t("audit.user"),
      width: 190,
      render: (r) => (
        <span>
          {r.user_nombre || r.user_email || "—"}
          {r.user_rol && <span className="ml-1.5 text-[11.5px] text-ink-tertiary">{t(`roles.${r.user_rol}`)}</span>}
        </span>
      ),
    },
    { key: "ip", label: t("audit.ip"), width: 120, render: (r) => <span className="text-ink-tertiary">{r.ip || "—"}</span> },
    {
      key: "diff",
      label: "",
      width: 90,
      align: "right",
      render: (r) => (
        <Button
          variant="ghost"
          size="sm"
          data-testid={`audit-detail-${r.id}`}
          onClick={() => setSelected(r)}
          className="h-7 rounded-[6px] px-2 text-[12px] text-brand"
        >
          {t("app.detail")}
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t("audit.title")} subtitle={t("audit.subtitle")} testId="audit-header" />

      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-white px-6 py-2.5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary" />
          <Input
            data-testid="audit-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("app.search")}
            className="h-8 w-[220px] rounded-[6px] pl-8 text-[13px]"
          />
        </div>
        <Select value={userId} onValueChange={(v) => { setUserId(v); setPage(1); }}>
          <SelectTrigger data-testid="audit-user-filter" className="h-8 w-[180px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("audit.user")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("app.all")}</SelectItem>
            {(facets?.usuarios || []).map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={entidad} onValueChange={(v) => { setEntidad(v); setPage(1); }}>
          <SelectTrigger data-testid="audit-entity-filter" className="h-8 w-[160px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("audit.entity")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("app.all")}</SelectItem>
            {(facets?.entidades || []).map((e) => (
              <SelectItem key={e} value={e}>
                {e}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={accion} onValueChange={(v) => { setAccion(v); setPage(1); }}>
          <SelectTrigger data-testid="audit-action-filter" className="h-8 w-[160px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("audit.action")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("app.all")}</SelectItem>
            {(facets?.acciones || []).map((a) => (
              <SelectItem key={a} value={a}>
                {t(`audit.actions.${a}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          data-testid="audit-from-input"
          type="date"
          value={desde}
          onChange={(e) => { setDesde(e.target.value); setPage(1); }}
          className="h-8 w-[145px] rounded-[6px] text-[13px]"
        />
        <Input
          data-testid="audit-to-input"
          type="date"
          value={hasta}
          onChange={(e) => { setHasta(e.target.value); setPage(1); }}
          className="h-8 w-[145px] rounded-[6px] text-[13px]"
        />
        <label className="ml-auto flex items-center gap-2 text-[12.5px] text-ink-secondary" data-testid="audit-system-toggle-label">
          <Switch
            data-testid="audit-system-toggle"
            checked={incluirSistema}
            onCheckedChange={(v) => { setIncluirSistema(v); setPage(1); }}
          />
          {t("audit.include_system")}
        </label>
      </div>

      <div className="flex min-h-0 flex-1 flex-col bg-white" data-testid="audit-page">
        <DataTable
          testId="audit-table"
          columns={columns}
          rows={data?.items}
          total={data?.total || 0}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          loading={isLoading}
          empty={
            <EmptyState
              icon={ShieldCheck}
              testId="audit-empty"
              title={t("audit.empty_title")}
              description={t("audit.empty_desc")}
            />
          }
        />
      </div>

      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent data-testid="audit-detail-sheet" className="w-[560px] max-w-[92vw] overflow-y-auto sm:max-w-[560px]">
          <SheetHeader>
            <SheetTitle className="text-[16px]">{t("audit.detail_title")}</SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-4 space-y-4">
              <div className="rounded-[8px] border border-line bg-surface-sunken p-3 text-[13px]">
                <p className="flex justify-between py-0.5">
                  <span className="text-ink-tertiary">{t("audit.action")}</span>
                  <StatusChip tone={TONES[selected.accion] || "gray"} label={t(`audit.actions.${selected.accion}`)} />
                </p>
                <p className="flex justify-between py-0.5">
                  <span className="text-ink-tertiary">{t("audit.entity")}</span>
                  <span>{selected.entidad}</span>
                </p>
                <p className="flex justify-between py-0.5">
                  <span className="text-ink-tertiary">{t("audit.entity_code")}</span>
                  <span className="font-medium">
                    {selected.entity_code || "—"}
                    {selected.entity_label && <span className="ml-1.5 font-normal text-ink-tertiary">· {selected.entity_label}</span>}
                  </span>
                </p>
                <p className="flex justify-between py-0.5">
                  <span className="text-ink-tertiary">{t("audit.user")}</span>
                  <span>{selected.user_nombre || selected.user_email || "—"}</span>
                </p>
                <p className="flex justify-between py-0.5">
                  <span className="text-ink-tertiary">{t("audit.date")}</span>
                  <span className="tabular-nums">{formatDateTime(selected.timestamp)}</span>
                </p>
                <p className="flex justify-between py-0.5">
                  <span className="text-ink-tertiary">{t("audit.ip")}</span>
                  <span>{selected.ip || "—"}</span>
                </p>
                {selected.detalle && (
                  <p className="mt-1.5 border-t border-line pt-1.5 text-[12.5px] text-ink">
                    {selected.detalle}
                  </p>
                )}
              </div>

              {selected.diff?.length ? (
                <div className="overflow-hidden rounded-[8px] border border-line" data-testid="audit-diff-table">
                  <table className="w-full text-[13px]">
                    <thead className="bg-surface-sunken">
                      <tr className="border-b border-line text-[11.5px] uppercase tracking-[0.04em] text-ink-tertiary">
                        <th className="px-3 py-2 text-left">{t("audit.field")}</th>
                        <th className="px-3 py-2 text-left">{t("audit.before")}</th>
                        <th className="px-3 py-2 text-left">{t("audit.after")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.diff.map((d) => {
                        const isQboRule = d.field === "facturacion_qbo"
                          && typeof d.before === "object" && typeof d.after === "object"
                          && d.before?.regla !== d.after?.regla;
                        const fieldLabel = isQboRule ? t("audit.qbo_rule_field") : d.field;
                        const beforeVal = isQboRule
                          ? t(`settings.accounting.rules.${d.before?.regla}`) : formatValue(d.before);
                        const afterVal = isQboRule
                          ? t(`settings.accounting.rules.${d.after?.regla}`) : formatValue(d.after);
                        return (
                          <tr key={d.field} className="border-b border-line last:border-0">
                            <td className="px-3 py-2 font-medium">{fieldLabel}</td>
                            <td className="px-3 py-2 text-state-critical">
                              <span className="rounded-[4px] bg-state-critical-soft px-1.5 py-0.5">{beforeVal}</span>
                            </td>
                            <td className="px-3 py-2 text-state-ok">
                              <span className="rounded-[4px] bg-state-ok-soft px-1.5 py-0.5">{afterVal}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-[13px] text-ink-tertiary">{t("audit.no_diff")}</p>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
